import { z } from 'zod'
import { PRODUCT_CATEGORY_IDS } from '../constants'
import { HEARD_FROM_SOURCES } from '../heard-from'
import {
  canTransitionQuoteRequest,
  quoteRequestStatusOf,
  QUOTE_REQUEST_STATUSES,
  type QuoteOfferStatus,
  type QuoteRequestStatus,
} from '../quote-status'
import type { Axis } from '../taxonomy'
import type { FormulaVersionDiff } from './formula-versions'
import { CURRENCIES, LANGUAGES, type Market } from './simulation'

/** submitted → quoted(견적 1개 이상) → accepted(사용자가 공장 하나 선택) → closed */
export const QUOTE_STATUSES = ['submitted', 'quoted', 'accepted', 'closed'] as const
export type QuoteStatus = (typeof QUOTE_STATUSES)[number]
/** 예전 네 값 중 하나인지 (관리자 PATCH 가 옛 화면이 보낸 값과 명세 상태를 가른다) */
export function isLegacyQuoteStatus(value: string): value is QuoteStatus {
  return (QUOTE_STATUSES as readonly string[]).includes(value)
}
/** 아직 마감 전인 견적 (계정 삭제 때 마감하는 대상, 탈퇴 창의 '진행 중 견적' 수) */
export const OPEN_QUOTE_STATUSES = [
  'submitted',
  'quoted',
  'accepted',
] as const satisfies readonly QuoteStatus[]

/**
 * 마감 사유 (C48). 새 상태값 없이 closed 에 붙인다 — 1.0.4 앱이 아는 상태 목록을 바꾸지 않으려고.
 * user_cancelled = 사용자가 앱에서 취소, no_offers = 공장 제안 0건인 채 관리자가 마감
 */
export const QUOTE_CLOSE_REASONS = ['user_cancelled', 'no_offers'] as const
export type QuoteCloseReason = (typeof QUOTE_CLOSE_REASONS)[number]

/**
 * 사용자가 취소할 수 있는 견적인지 (C48 대표 결정): 허용 전이 표에서 고객이 cancelled 로 갈 수 있는 상태
 * (접수·공장 확인·추가 정보 요청)이고 공장 제안이 0건. 서버는 같은 조건을 UPDATE 문에 걸어 관리자 제안 등록과
 * 겹쳐도 한쪽만 이기게 하고, 앱은 이 함수로 버튼을 그린다
 */
export function quoteCancellable(
  quote: Pick<QuoteView, 'status' | 'requestStatus' | 'closeReason' | 'selectedOfferId' | 'offers'>,
): boolean {
  return (
    canTransitionQuoteRequest(quoteRequestStatusOf(quote), 'cancelled', 'customer') &&
    quote.offers.length === 0
  )
}

const money = z.number().nonnegative().max(1e9)

/** 보관 방식. 견적 요청서와 공장 목록(factories.storage)이 같은 값을 쓴다 */
export const QUOTE_STORAGE_TYPES = ['ambient', 'refrigerated', 'frozen'] as const
export type QuoteStorageType = (typeof QUOTE_STORAGE_TYPES)[number]

/** 비용 항목 빈도 (USDA 스타트업 비용표의 Expense Frequency) */
export const QUOTE_FEE_FREQUENCIES = ['one_time', 'per_batch', 'monthly', 'annual'] as const
export type QuoteFeeFrequency = (typeof QUOTE_FEE_FREQUENCIES)[number]

/**
 * 견적 요청서 — 공동 포장(co-packer) 레시피 카드의 공통 분모 (Mad Will's Co-Packer Recipe Card 기준):
 * 브랜드·제품·용기/뚜껑·규정 요구(클린라벨/Non-GMO/인증)·대체재 허용·재료+중량·총 배합 중량·공정(온도/시간)·장비.
 * 시뮬레이션 결과에서 자동으로 채우고 사용자가 수량·목표가·포장·연락처를 손본다. 제출하면 어드민이 공장 견적(result)을 입력한다.
 */
export const quoteRequestDocSchema = z.object({
  /** 브랜드/회사명 (라벨의 "Manufactured for") */
  brandName: z.string().max(80).default(''),
  productName: z.string().min(1).max(80),
  categoryId: z.enum(PRODUCT_CATEGORY_IDS),
  format: z.string().max(60).default(''),
  netWeight: z.object({ amount: z.number().positive(), unit: z.string().min(1).max(10) }),
  storage: z.enum(QUOTE_STORAGE_TYPES),
  shelfLifeDays: z.number().int().min(1).max(730),
  /** 희망 생산 수량 (판매 단위) */
  quantity: z.number().int().min(1).max(10_000_000),
  /** 목표 단가 (없으면 null) */
  targetUnitPrice: money.nullable().default(null),
  currency: z.enum(CURRENCIES),
  ingredients: z
    .array(
      z.object({
        name: z.string().min(1).max(80),
        qty: z.number().positive(),
        unit: z.string().min(1).max(20),
      }),
    )
    .max(25),
  /** 용기 종류·사이즈 (예: "12 oz 유리병", "스탠드 파우치 200 g") */
  container: z.string().max(80).default(''),
  /** 뚜껑/마감 (예: "메탈 러그 캡", "지퍼") */
  lid: z.string().max(80).default(''),
  packagingNotes: z.string().max(500).default(''),
  /** 규정·인증 요구 */
  cleanLabel: z.boolean().default(false),
  gmoFree: z.boolean().default(false),
  certifications: z.array(z.string().min(1).max(40)).max(10).default([]),
  /** 원료 대체 허용 (예: 다진 것 대신 퓨레) */
  substitutesOk: z.boolean().default(true),
  /** 재료 투입 총 중량 g (확인된 질량 단위만 합산, 밀도 없는 부피 입력은 null) */
  totalRecipeWeightG: z.number().nonnegative().nullable().default(null),
  /** 공정 요약 — 단계명·온도·시간 */
  steps: z
    .array(
      z.object({
        title: z.string().min(1).max(60),
        temp: z.string().max(60).default(''),
        time: z.string().max(60).default(''),
      }),
    )
    .max(10)
    .default([]),
  /** 필요한 특수 장비 */
  equipment: z.array(z.string().min(1).max(120)).max(10).default([]),
  allergens: z.array(z.string().min(1).max(40)).max(14).default([]),
  /** 상품 설명 (商品説明) — 결과의 한 줄 소개에서 시작, 사용자가 손볼 수 있다 */
  productDescription: z.string().max(1000).default(''),
  recipeVersion: z.string().max(40).default('v1'),
  notes: z.string().max(1000).default(''),
  contact: z.object({
    name: z.string().min(1).max(60),
    phone: z.string().max(40).default(''),
    email: z.string().max(120).default(''),
  }),
  language: z.enum(LANGUAGES).default('en'),
  /** 어떻게 알고 왔나 (선택, C56). 운영자만 본다. 옛 요청·옛 앱에는 없다 */
  heardFrom: z.enum(HEARD_FROM_SOURCES).optional(),
})
export type QuoteRequestDoc = z.infer<typeof quoteRequestDocSchema>

export const createQuoteSchema = z.object({
  simulationId: z.string().min(1),
  request: quoteRequestDocSchema,
})
export type CreateQuoteInput = z.infer<typeof createQuoteSchema>

/**
 * POST /quotes/waitlist (로드맵 P7). 견적을 받지 않는 시장 제품의 견적 버튼에서 '대기 명단 등록'을 누르면 보낸다.
 * 시장·분류는 서버가 제품에서 읽는다(앱이 보낸 값을 믿지 않는다). 이메일은 받지 않는다 — 회원 id 로 중복 등록을 막는다
 */
export const quoteWaitlistJoinSchema = z.object({ simulationId: z.string().min(1).max(64) })
export type QuoteWaitlistJoinInput = z.infer<typeof quoteWaitlistJoinSchema>
/** POST /quotes/waitlist 응답. alreadyJoined: 같은 시장·분류로 이미 등록돼 있어 새로 저장하지 않았다 */
export interface QuoteWaitlistJoinResult {
  market: Market
  alreadyJoined: boolean
}
/** GET /admin/quote-waitlist — 시장별 대기 명단에 등록한 회원 수 (한 회원이 여러 분류로 등록해도 1명) */
export interface AdminQuoteWaitlistSummary {
  markets: { market: Market; users: number }[]
}

/** 공장 샘플 조건 (2026-10-25). 고르기 전에 맛을 확인할 수 있는지 사용자가 비교표에서 본다 */
export const QUOTE_SAMPLE_AVAILABILITY = ['free', 'paid', 'none'] as const
export type QuoteSampleAvailability = (typeof QUOTE_SAMPLE_AVAILABILITY)[number]
export const quoteSampleSchema = z
  .object({
    /** free = 무료 샘플 · paid = 유료 샘플 · none = 샘플 없음(첫 생산만) */
    availability: z.enum(QUOTE_SAMPLE_AVAILABILITY),
    /** 유료 샘플 금액 (견적 통화). 유료인데 아직 모르면 null, 무료·없음이면 반드시 null */
    price: money.nullable().default(null),
    /** 샘플을 받기까지 걸리는 날 수. 모르면 null */
    leadTimeDays: z.number().int().min(0).max(365).nullable().default(null),
  })
  // 무료·없음에 금액이 붙으면 화면마다 다르게 읽힌다. 유료+금액 미정(null)은 관리자 폼이 "모르면 비워 두세요"로
  // 받는 정상 상태라 막지 않는다. 저장된 견적은 서버·앱이 이 스키마로 다시 읽지 않으므로(원본 JSON 그대로 내려감)
  // 옛 행은 깨지지 않고, 관리자가 그 견적을 고쳐 저장할 때만 이 검사를 받는다.
  .refine((sample) => sample.availability === 'paid' || sample.price === null, {
    path: ['price'],
    message: 'Only a paid sample can have a price',
  })
export type QuoteSample = z.infer<typeof quoteSampleSchema>

/**
 * 어드민이 입력하는 공장 견적 결과 — 商品企画御見積書(카네마스) + Mad Will's 비용 구조 + USDA 비용표의 공통 분모:
 * 공장 정보, 제조 단가, MOQ, 케이스 입수, 발주 단위, 납기, 유효기한, 참고 판매가, 결제 조건, 빈도별 비용 항목.
 */
export const quoteResultSchema = z.object({
  /**
   * 공장 목록(factories)에서 고른 공장의 id. 없으면 공장명을 손으로 적은 견적이다 (2026-10-29 이전 견적은 모두 없음).
   * 공장명·소재지·인증은 고른 그때의 값을 아래 칸에 그대로 남긴다 — 공장 목록을 고쳐도 보낸 견적은 바뀌지 않는다
   */
  factoryId: z.string().min(1).max(64).optional(),
  factoryName: z.string().max(80).default(''),
  factoryLocation: z.string().max(80).default(''),
  /** 공장 인증 (HACCP, FDA 등록, 유기 등) */
  factoryCertifications: z.array(z.string().min(1).max(40)).max(10).default([]),
  currency: z.enum(CURRENCIES),
  /** 단위당 제조 단가 */
  unitPrice: money,
  /** 최소 주문 수량 (판매 단위) */
  moq: z.number().int().min(0).max(10_000_000),
  /** 케이스 입수 (ケース入数). 0 = 미정 */
  casePack: z.number().int().min(0).max(10_000).default(0),
  /** 발주 단위 설명 (発注単位, 예: "1 c/s = 30개 단위 직송") */
  orderUnit: z.string().max(80).default(''),
  leadTimeDays: z.number().int().min(0).max(3650),
  /** 견적 유효 기한 (YYYY-MM-DD). 없으면 null */
  validUntil: z.string().max(10).nullable().default(null),
  /** 참고 판매가 (参考売価, 세전). 없으면 null */
  suggestedRetail: money.nullable().default(null),
  /** 결제 조건 (예: "발주 시 50%, 생산 완료 시 50%") */
  paymentTerms: z.string().max(200).default(''),
  /** JAN/UPC 바코드 (발급 뒤 입력). 없으면 '' */
  barcode: z.string().max(40).default(''),
  /** 포장 치수 mm (縦×横×高). 없으면 null */
  dimensionsMm: z
    .object({
      w: z.number().int().min(0).max(5000),
      d: z.number().int().min(0).max(5000),
      h: z.number().int().min(0).max(5000),
    })
    .nullable()
    .default(null),
  /** 참고 판매가 세율 % (税込 계산용). 0 이면 세후가를 안 보여준다 */
  taxRatePct: z.number().min(0).max(30).default(0),
  /** 공장이 정리한 상품 설명 (商品説明). 비어 있으면 요청서의 설명을 쓴다 */
  description: z.string().max(1000).default(''),
  /** 샘플 조건. null = 아직 모름 (2026-10-25 이전 견적도 null) */
  sample: quoteSampleSchema.nullable().default(null),
  /** 단가 외 비용 — 빈도별 (개발비·테스트 배치·라벨 판형·영양성분 분석: 1회성 / 보관료: 월 / 인증·감사: 연) */
  lines: z
    .array(
      z.object({
        label: z.string().min(1).max(60),
        amount: money,
        frequency: z.enum(QUOTE_FEE_FREQUENCIES).default('one_time'),
      }),
    )
    .max(20)
    .default([]),
  notes: z.string().max(2000).default(''),
})
export type QuoteResult = z.infer<typeof quoteResultSchema>

export const quoteStatusPatchSchema = z.object({
  /**
   * 갈 상태. 예전 값(submitted·quoted·accepted·closed)은 옛 관리자 화면이 보낸다. 명세 상태(reviewing·needs_information 등)는
   * 관리자 settings.quoteStatuses 가 켜져 있을 때만 받는다. 서버는 둘 다 명세 상태로 바꿔 허용 전이 표로 본다
   */
  status: z.enum([...QUOTE_STATUSES, ...QUOTE_REQUEST_STATUSES]),
  /** 상태를 바꾼 이유 (C04). 관리자 작업 기록에만 남는다. 마감은 선택(옛 관리자 화면은 안 보냄), 다시 열기는 필수 */
  reason: z.string().trim().max(500).optional(),
})

/**
 * 견적 1건 = 소모성 인앱 상품 1개 ($19.99). App Store · Play · RevenueCat 대시보드에 같은 id 로 등록한다.
 * 서버는 RevenueCat 의 non_subscriptions 로 구매를 확인하고 트랜잭션 1건당 견적 1건을 붙인다.
 * Basic 구독자에게는 20% 할인 상품(quote_credit_basic)을 띄운다. Pro 는 매달 1건 포함 (quota.includedQuotes).
 */
export const QUOTE_CREDIT_PRODUCT_ID = 'quote_credit'
export const QUOTE_CREDIT_BASIC_PRODUCT_ID = 'quote_credit_basic'
export const QUOTE_CREDIT_PRODUCT_IDS = [
  QUOTE_CREDIT_PRODUCT_ID,
  QUOTE_CREDIT_BASIC_PRODUCT_ID,
] as const

/** 결제 필요 응답의 error.code (HTTP 402) */
export const QUOTE_PAYMENT_CODE = 'QUOTE_PAYMENT_REQUIRED'

/** POST /quotes/:id/select 409 의 error.code — 마감(closed)된 견적이라 공장을 바꿀 수 없음 */
export const QUOTE_CLOSED_CODE = 'QUOTE_CLOSED'
/** POST /quotes/:id/cancel 409 — 그 사이 공장 제안이 왔거나 마감돼 취소할 수 없음. 앱은 견적을 다시 불러온다 */
export const QUOTE_NOT_CANCELLABLE_CODE = 'QUOTE_NOT_CANCELLABLE'
/** POST /quotes/:id/select 409 — 고른 공장 견적의 유효 기한(validUntil)이 지났음 */
export const QUOTE_OFFER_EXPIRED_CODE = 'QUOTE_OFFER_EXPIRED'
/** POST /quotes/:id/select 409 — 앱이 본 뒤 운영자가 그 공장 견적을 고쳤음 (offerRevision 이 다름) */
export const QUOTE_OFFER_CHANGED_CODE = 'QUOTE_OFFER_CHANGED'

/** GET /quotes/credits */
export interface QuoteCreditsView {
  /** 아직 견적에 쓰지 않은 크레딧 수 (구매 + 플랜 포함분) */
  available: number
  /** 이 사용자 플랜의 견적 상품 id */
  productId: string
  /** false 면 서버에 RevenueCat 키가 없어 결제 없이 요청을 받는다 (로컬 개발) */
  paymentsEnabled: boolean
}

/** 공장 견적 1건 (한 요청에 여러 공장이 답할 수 있다, 2026-09-16) */
export interface QuoteOfferView extends QuoteResult {
  id: string
  createdAt: string
  /** 이 견적 내용의 판. 운영자가 고치면 바뀐다. 고를 때 offerRevision 으로 돌려준다. 옛 서버에는 없다 */
  revision?: string
  /** 제안 상태 (명세 P0-02, quoteOfferStatusOf). 옛 서버에는 없다 */
  offerStatus?: QuoteOfferStatus
}

export const selectOfferSchema = z.object({
  offerId: z.string().min(1),
  /** 앱이 화면에 그린 견적의 revision. 옛 앱은 안 보낸다 — 그러면 유효 기한만 본다 */
  offerRevision: z.string().min(1).max(32).optional(),
})

export interface QuoteView {
  id: string
  userId: string
  simulationId: string
  /** 예전 상태 네 값. 1.0.3~1.0.7 앱이 읽는다 — 새 상태가 생겨도 이 칸에는 예전 값만 간다 */
  status: QuoteStatus
  /** 명세 상태 (quoteRequestStatusOf). 옛 서버에는 없다 — 그러면 status 에서 끌어낸다 */
  requestStatus?: QuoteRequestStatus
  request: QuoteRequestDoc
  /** @deprecated offers[0] 와 같다 — 옛 클라이언트 호환용 */
  result: QuoteResult | null
  offers: QuoteOfferView[]
  /** 사용자가 고른 공장 견적 (accepted) */
  selectedOfferId: string | null
  createdAt: string
  quotedAt: string | null
  /** 연결 제품(시뮬레이션 입력)의 분류 — 앱 견적 상세의 세부 분류 사진 (C32). 옛 서버·생성 응답에는 없다 */
  axis?: Axis
  majorId?: string | null
  subId?: string | null
  /** 마감 사유 (C48). 사유 없이 마감됐거나 옛 서버면 null·없음 */
  closeReason?: QuoteCloseReason | null
  /** 이 견적에 쓴 견적권을 돌려줬는지 (C48). 옛 서버면 없음 = 돌려주지 않음 */
  creditReturned?: boolean
  /**
   * 견적이 고정한 배합 버전 (C43). GET /quotes/:id 와 관리자 상세에만 싣는다(목록·접수·선택 응답에는 없다).
   * 고정이 없거나(버전 기록 꺼짐·버전 없던 제품) 그 버전·제품이 지워졌으면 null, 옛 서버면 없음
   */
  formulaBasis?: QuoteFormulaBasis | null
}

/** 견적 기준 배합 (C43). 접수 순간의 배합 버전과 지금 저장된 배합의 관계 */
export interface QuoteFormulaBasis {
  versionId: string
  /** 그 버전의 번호 (화면의 'v3') */
  number: number
  /** 지금 저장된 배합이 그 버전과 같은지. 거짓이면 화면이 '이전 기준'으로 보인다 */
  current: boolean
  /** current 가 거짓일 때 그 버전 → 지금 배합의 차이 (compareFormulaVersions). 같으면 null */
  diff: FormulaVersionDiff | null
}
